import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlContainer, UntypedFormGroup } from '@angular/forms';
import { SpinnerService } from '@core/services/spinner.service';
import { MergeDocumentModalPreviewComponent } from '@features/application-view/application-attachments/merge-document-preview-modal/merge-document-preview-modal.component';
import { DocumentTemplateService } from '@features/document-templates/document-template.service';
import { DocumentTemplateForUi } from '@features/document-templates/document-template.typing';
import { EmailService } from '@features/system-emails/email.service';
import { ClientEmailTemplateForUI, Email, EmailNotificationType, SimpleEmail } from '@features/system-emails/email.typing';
import { ViewEmailModalComponent } from '@features/system-emails/view-email-modal/view-email-modal.component';
import { UserService } from '@features/users/user.service';
import { SelectOption } from '@yourcause/common/core-forms';
import { GenericFile, maxFileSizeForEmailAttachments } from '@yourcause/common/files';
import { I18nService } from '@yourcause/common/i18n';
import { LogService } from '@yourcause/common/logging';
import { ModalFactory } from '@yourcause/common/modals';
import { NotifierService } from '@yourcause/common/notifier';
import { ArrayHelpersService } from '@yourcause/common/utils';

@Component({
    selector: 'gc-select-email-template',
    templateUrl: './select-email-template.component.html',
    styleUrls: ['./select-email-template.component.scss'],
    standalone: false
})
export class SelectEmailTemplateComponent implements OnInit, OnChanges {
  @Input() forceFormGroupValueToDefault = true;
  // Unless you set this boolean to false in the template,
  // the formControl value will be set to the default template
  @Input() programId: number;
  @Input() controlName: 'clientEmailTemplateId'|'cashClientEmailTemplateId'|'inKindClientEmailTemplateId' = 'clientEmailTemplateId';
  @Input() ccControlName: 'cc'|'cashCC'|'inKindCC'|'ccEmails' = 'ccEmails';
  @Input() bccControlName: 'bcc'|'cashBCC'|'inKindBCC'|'bccEmails' = 'bccEmails';
  @Input() customMessageControlName = 'customMessage';
  @Input() customMessageLabel = this.i18n.translate(
    'common:textCustomMessageInEmail',
    {},
    'Custom message in email'
  );
  @Input() attachmentsControlName = 'attachments';
  @Input() allowMailMerge = true;
  @Input() applicationIds: number[] = [];
  @Input() emailType: EmailNotificationType;
  @Input() notifyApplicant = true;
  @Input() formGroup: UntypedFormGroup;
  @Input() allowAttachments = true;
  @Input() showSingleOption = false;
  @Input() activeTempsFromChangesMap: number[] = [];
  @Input() allowCCandBCC = true;
  @Input() forceUpdateEmailTemplates = true;
  @Output() onPreviewModalOpenOrClose = new EventEmitter<boolean>();
  @Output() onDefaultAttachmentsChange = new EventEmitter<GenericFile[]>();

  attachments: GenericFile[] = [];
  maxAttachmentsFileSize = maxFileSizeForEmailAttachments;
  private maxDescription = 170;
  emailOptions: SelectOption[] = [];
  templates: SimpleEmail[];
  userLang = this.userService.getCurrentUserCulture();
  showCcBcc = false;
  allowCcUpdates = false;
  mailMergeTemplate: DocumentTemplateForUi;
  hideModal = false;
  emailOptionsMap: Record<number, SimpleEmail> = {};
  missingCustomMessageToken = false;
  selectedTemplateName = '';
  standardEmailName = '';
  afterInit = false;

  constructor (
    private logger: LogService,
    private modalFactory: ModalFactory,
    private i18n: I18nService,
    private notifier: NotifierService,
    private spinnerService: SpinnerService,
    private arrayHelper: ArrayHelpersService,
    private controlContainer: ControlContainer,
    private emailService: EmailService,
    private userService: UserService,
    private documentTemplateService: DocumentTemplateService
  ) { }

  get templateMap () {
    return this.emailService.templateMap[this.emailType];
  }

  get email (): Email {
    if (this.emailService.templateMap) {
      return this.templateMap.email;
    }

    return null;
  }

  async ngOnInit () {
    this.spinnerService.startSpinner();
    if (!this.formGroup) {
      this.formGroup = <UntypedFormGroup>this.controlContainer.control;
    }
    await Promise.all([
      this.emailService.setEmails(),
      this.documentTemplateService.setDocumentTemplates()
    ]);
    await this.prepHelpers();
    this.spinnerService.stopSpinner();
    this.afterInit = true;
  }

  async ngOnChanges (changes: SimpleChanges) {
    if (this.afterInit) {
      this.spinnerService.startSpinner();
      if (changes.emailType || changes.notifyApplicant || changes.programId) {
        await this.prepHelpers();
      }
      this.spinnerService.stopSpinner();
    }
  }

  async prepHelpers () {
    // if simple changes includes emailType
    let templates: SimpleEmail[] = [];
    try {
      // force an update to the template map
      await this.emailService.setTemplateMap(
        this.emailType,
        this.forceUpdateEmailTemplates,
        this.userLang
      );
      if (this.programId) {
        // if this is a program-specific email, update that map as well
        await this.emailService.setProgramTemplatesByType(
          this.programId,
          this.emailType
        );

        templates = this.templateMap.programTemplates[this.programId]
          .map(template => this.getSimpleEmail(template, true));
      } else {
        const standard: SimpleEmail = {
          id: 0,
          emailNumber: '' + this.email.emailNotificationType,
          subject: this.email.subject,
          emailNotificationType: this.email.emailNotificationType,
          audienceType: this.email.audienceType,
          title: this.email.title,
          body: this.templateMap.template,
          description: this.email.description,
          active: true,
          ccEmails: [],
          bccEmails: [],
          emailAttachments: [],
          allowCarbonCopyUpdates: false,
          supportsCarbonCopy: false,
          allowDocumentTemplates: false,
          buttonText: this.emailService.getButtonText(this.email, this.templateMap.template)
        };
        templates = [standard].concat(this.templateMap.clientTemplates
          .map((template) => this.getSimpleEmail(template, false)));
      }
      this.setEmailOptions(templates, this.activeTempsFromChangesMap);
      templates = templates.filter((temp) => {
        return !!temp.active || this.activeTempsFromChangesMap.includes(temp.id);
      });
      this.templates = this.arrayHelper.sort(templates, 'id');
      const standardTemplate = this.templates.find((temp) => temp.id === 0);
      this.standardEmailName = standardTemplate?.emailNumber ?? '';

      if (this.programId) {
        await this.setDefaultTemplate();
      }
    } catch (e) {
      this.logger.error(e);
      this.notifier.error(this.i18n.translate(
        'GLOBAL:textErrorLoadingEmail',
        {},
        'There was an error loading the copies'
      ));
    }
  }

  getSelectedEmailTemplateId (): number {
    const control = this.formGroup.get(this.controlName);
    if (control) {
      return control.value || 0;
    }

    return 0;
  }

  async setDefaultTemplate () {
    const formControlVal = this.getSelectedEmailTemplateId();
    if (this.forceFormGroupValueToDefault || !formControlVal) {
      const defaultTemplateId = await this.emailService.getProgramDefault(
        this.programId,
        this.emailType
      );
      setTimeout(() => {
        this.formGroup.get(this.controlName).setValue(defaultTemplateId);
        this.onEmailSelect();
      });
    } else {
      this.onEmailSelect();
    }
  }

  setEmailOptions (templates: SimpleEmail[], activeTempsFromChangesMap: number[]) {
    const inactiveIds = templates.filter((temp) => {
      return !temp.active;
    }).map((temp) => temp.id);
    this.emailOptionsMap = {};
    this.emailOptions = templates.map((temp) => {
      this.emailOptionsMap[temp.id] = {
        ...temp,
        description: temp.description.length > this.maxDescription ?
        temp.description.slice(0, this.maxDescription) + '...' :
        temp.description
      };

      return {
        value: temp.id,
        label: temp.emailNumber
      };
    }).filter((option) => {
      return !inactiveIds.includes(option.value) || activeTempsFromChangesMap.includes(option.value);
    });
  }

  viewProgramEmail (id: number) {
    const template = this.templates.find((temp) => {
      return temp.id === id;
    });

    this.viewEmail(template);
  }

  async viewEmail (email: SimpleEmail) {
    this.onPreviewModalOpenOrClose.emit(true);
    this.hideModal = true;
    await this.modalFactory.open(
      ViewEmailModalComponent,
      { email }
    );
    this.hideModal = false;
    this.onPreviewModalOpenOrClose.emit(false);
  }

  getSimpleEmail (
    email: ClientEmailTemplateForUI,
    forProgram: boolean
  ): SimpleEmail {
    const type = this.emailType;
    const found = this.emailService.emails.find((item) => {
      return item.emailNotificationType === type;
    });

    return {
      subject: email.subject,
      body: email.body,
      emailNumber: email.emailNumber,
      description: email.description,
      active: forProgram ? email.isProgramTemplateActive : email.isClientTemplateActive,
      id: email.clientEmailTemplateId,
      title: email.title,
      ccEmails: email.ccEmails,
      bccEmails: email.bccEmails,
      emailAttachments: email.emailAttachments,
      allowCarbonCopyUpdates: email.allowCarbonCopyUpdates,
      emailNotificationType: type,
      audienceType: found.audienceType,
      supportsCarbonCopy: found.supportsCarbonCopy,
      allowDocumentTemplates: found.allowDocumentTemplates,
      documentTemplates: email.documentTemplates,
      buttonText: this.emailService.getButtonText(email, email.body)
    };
  }

  onEmailSelect () {
    const templateId = this.getSelectedEmailTemplateId();
    let missingCustomMessageToken = false;
    const isCustomCopy = templateId !== 0;
    const found = this.templates.find((temp) => {
      return temp.id === templateId;
    });
    this.selectedTemplateName = found.emailNumber;
    if (isCustomCopy && this.notifyApplicant && this.allowCCandBCC) {
      this.showCcBcc = found.supportsCarbonCopy &&
        !!(
          (found.ccEmails.length || found.bccEmails.length) ||
          found.allowCarbonCopyUpdates
        );
      this.updateAttachmentsList(found.emailAttachments);
      // If custom message supported,
      // we want to check the custom copy to make sure it contains the custom message token
      // If not, display a warning and don't allow custom message input
      if (!!this.customMessageControlName) {
        const hasToken = this.emailService.doesTemplateHaveCustomMessageToken(found.body, this.emailType);
        if (!hasToken) {
          missingCustomMessageToken = true;
        }
      }
    } else {
      // Cc Bcc not available on standard email (0)
      this.showCcBcc = false;
      this.updateAttachmentsList([]);
    }
    this.mailMergeTemplate = this.getMailMergeTemplate();
    this.allowCcUpdates = this.showCcBcc ?
      found.allowCarbonCopyUpdates :
      false;
    const ccControl = this.formGroup.get(this.ccControlName);
    const bccControl = this.formGroup.get(this.bccControlName);
    if (ccControl && bccControl) {
      ccControl.setValue(
        this.showCcBcc ? found.ccEmails : []
      );
      bccControl.setValue(
        this.showCcBcc ? found.bccEmails : []
      );
    }
    this.missingCustomMessageToken = missingCustomMessageToken;
  }

  updateAttachmentsFormControl (attachments: GenericFile[]) {
    const attachmentsControl = this.formGroup.get(this.attachmentsControlName);
    attachmentsControl?.setValue(attachments);
  }

  updateAttachmentsList (attachments: GenericFile[]) {
    this.attachments = [
      ...attachments
    ];
    this.updateAttachmentsFormControl(this.attachments);
  }

  addAttachment (payload: {attachment: GenericFile}) {
    this.attachments = [
      ...this.attachments,
      payload.attachment
    ];
    this.updateAttachmentsFormControl(this.attachments);
  }

  removeAttachment (index: number) {
    this.attachments = [
      ...this.attachments.slice(0, index),
      ...this.attachments.slice(index + 1)
    ];
    this.updateAttachmentsFormControl(this.attachments);
  }

  getMailMergeTemplate (): DocumentTemplateForUi {
    const selectedEmailId = this.getSelectedEmailTemplateId();
    if (selectedEmailId !== 0 && this.notifyApplicant) {
      const template = this.templates.find((temp) => {
        return temp.id === selectedEmailId;
      });
      if (template?.documentTemplates?.length > 0) {
        const selectedDocId = template.documentTemplates[0]?.documentTemplateId;
        const foundTemplate = this.documentTemplateService.documentTemplates
          .find((temp) => {
            return temp.id === selectedDocId;
          });
        if (foundTemplate) {
          return foundTemplate;
        }
      }
    }

    return null;
  }

  async openMailMergePreview () {
    this.onPreviewModalOpenOrClose.emit(true);
    await this.modalFactory.open(
      MergeDocumentModalPreviewComponent,
      {
        templateId: this.mailMergeTemplate.id,
        applicationIds: this.applicationIds
      }
    );
    this.onPreviewModalOpenOrClose.emit(false);
  }
}
